import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from "@mui/material";
import { useController } from "react-hook-form";
import { observer } from "mobx-react";
import adresseVaelgerService from "../../Services/AdresseVaelgerService";

const DEBOUNCE_MS = 300;

// Adressevælgeren returnerer først vejnavne, så husnumre og til sidst adresser.
// Kun et fund af den endelige type udfylder feltet - de andre bruges til at søge videre.
const isFinalItem = (item, searchType) =>
    item.type === "adresse" || (item.type === "husnummer" && searchType === "husnumre");

const AdresseVaelgerAutocomplete = ({
    control,
    id,
    label,
    options,
    maxWidth,
    type,
    fullwidth,
    defaultAddressValue,
    adgangsadresserOnly = false,
    disabled = false,
}) => {
    const searchType = adgangsadresserOnly ? "husnumre" : "adresser";
    const { field, fieldState } = useController({ name: id, control, rules: options, defaultValue: "" });
    const [searchText, setSearchText] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (defaultAddressValue && !fieldState.isDirty) {
            field.onChange(defaultAddressValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultAddressValue]);

    useEffect(() => {
        if (!searchText.trim()) {
            setSuggestions([]);
            return;
        }
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const fund = await adresseVaelgerService.search(searchText, { searchType, signal: controller.signal });
                // Et husnummer med kun én adresse vælges direkte, så brugeren ikke skal klikke to gange.
                if (fund.length === 1 && isFinalItem(fund[0], searchType) && fund[0].titel.startsWith(searchText)) {
                    selectFinal(fund[0]);
                } else {
                    setSuggestions(fund);
                }
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error('Error fetching data:', error);
                    setSuggestions([]);
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }, DEBOUNCE_MS);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, searchType]);

    const selectFinal = (item) => {
        field.onChange(item.titel);
        setSuggestions([]);
        setOpen(false);
    };

    const handleChange = (event, item) => {
        if (!item) return;
        if (isFinalItem(item, searchType)) {
            selectFinal(item);
        } else {
            field.onChange(item.titel);
            setSearchText(item.titel);
            setOpen(true);
        }
    };

    const handleInputChange = (event, value, reason) => {
        // "reset" sendes af MUI når en mulighed vælges eller feltet mister fokus - det håndteres i handleChange.
        if (reason === "reset") return;
        field.onChange(value);
        setSearchText(value);
        setOpen(!!value);
    };

    return (
        <Autocomplete
            disableClearable
            id="adressevaelger"
            value={null}
            inputValue={field.value ?? ""}
            options={suggestions}
            getOptionLabel={(item) => item?.titel ?? ""}
            isOptionEqualToValue={(item, value) => item.titel === value.titel}
            renderOption={(props, item) => (
                <li {...props} key={`${item.type}-${item.id ?? item.titel}`}>{item.titel}</li>
            )}
            filterOptions={(x) => x}
            open={open && suggestions.length > 0}
            onOpen={() => setOpen(true)}
            onClose={(event, reason) => {
                if (reason !== "selectOption") setOpen(false);
            }}
            clearOnBlur={false}
            loading={loading}
            loadingText="Søger..."
            noOptionsText="Ingen mulighed"
            onChange={handleChange}
            onInputChange={handleInputChange}
            disabled={disabled}
            fullWidth={!!fullwidth}
            renderInput={(params) =>
                <TextField {...params}
                           name={field.name}
                           inputRef={field.ref}
                           onBlur={field.onBlur}
                           style={!!maxWidth ? { maxWidth: maxWidth } : { maxWidth: "20rem" }}
                           variant="outlined"
                           fullWidth={!!fullwidth}
                           error={!!fieldState.error}
                           label={label}
                           type={!!type ? type : undefined}
                           helperText={fieldState.error?.message}
                />}
        />
    );
};

export default observer(AdresseVaelgerAutocomplete);
